import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContacts,
  getContactsStats,
  getContactImports,
  createContact,
  importContactsCsv,
  deleteContact,
  updateContact,
  getContactTags,
  createContactTag,
  updateContactTag,
  deleteContactTag,
  getEmailSuppressions,
  clearEmailSuppression,
} from "api/bMarketingApi";

export function useContacts(organisationId, filters = {}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["contacts", organisationId, filters],
    queryFn: () => getContacts(organisationId, filters).then((res) => res.data),
    enabled: !!organisationId,
  });

  return {
    contacts: data?.results || [],
    pagination: {
      count: data?.count || 0,
      numPages: data?.num_pages || 1,
      currentPage: data?.current_page || 1,
    },
    isLoading,
    error,
    refetch,
  };
}

export function useContactsStats(organisationId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["contactsStats", organisationId],
    queryFn: () => getContactsStats(organisationId).then((res) => res.data),
    enabled: !!organisationId,
    refetchInterval: 60000, // Refetch every 60 seconds
  });

  return {
    stats: data || {
      total_contacts: 0,
      email_opted_in: 0,
      sms_opted_in: 0,
      whatsapp_opted_in: 0,
      email_suppressed: 0,
      invalid_emails: 0,
      email_validation: {
        valid: 0, invalid: 0, risky: 0, unknown: 0,
        pending: 0, processing: 0, missing_state: 0, no_email: 0,
      },
      suppressions_by_reason: {},
      by_source: {},
      by_location: [],
    },
    isLoading,
    error,
    refetch,
  };
}

export function useEmailSuppressions(organisationId, filters = {}, enabled = true) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["emailSuppressions", organisationId, filters],
    queryFn: () => getEmailSuppressions(organisationId, filters).then((res) => res.data),
    enabled: !!organisationId && enabled,
  });

  return {
    suppressions: data?.results || [],
    pagination: {
      count: data?.count || 0,
      numPages: data?.num_pages || 1,
      currentPage: data?.current_page || 1,
    },
    canManage: Boolean(data?.can_manage),
    isLoading,
    error,
    refetch,
  };
}

export function useClearEmailSuppression() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ suppressionId, data }) => clearEmailSuppression(suppressionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailSuppressions"] });
      queryClient.invalidateQueries({ queryKey: ["contactsStats"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["campaignAudienceEstimate"] });
    },
  });
}

export function useContactImports(organisationId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["contactImports", organisationId],
    queryFn: () => getContactImports(organisationId).then((res) => res.data),
    enabled: !!organisationId,
    refetchInterval: 5000,
  });

  return {
    contactImports: data || [],
    isLoading,
    error,
    refetch,
  };
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId) => deleteContact(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contactsStats"] });
      queryClient.invalidateQueries({ queryKey: ["contactImports"] });
    },
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contactsStats"] });
    },
  });
}

export function useImportContactsCsv() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => importContactsCsv(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contactsStats"] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, data }) => updateContact(contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contactsStats"] });
    },
  });
}

export function useContactTags(organisationId, options = {}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["contactTags", organisationId],
    queryFn: () => getContactTags(organisationId).then((res) => res.data),
    enabled: !!organisationId && options.enabled !== false,
  });

  return {
    contactTags: data || [],
    isLoading,
    error,
    refetch,
  };
}

export function useCreateContactTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createContactTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactTags"] });
    },
  });
}

export function useUpdateContactTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, data }) => updateContactTag(tagId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactTags"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useDeleteContactTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId) => deleteContactTag(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactTags"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}
